import { useEffect, useState } from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import FormInput from "../Common/FormInputs/MuiTextField";
import AppRadioGroup from "../Common/FormInputs/AppRadioGroup";
import FullScreenLoader from "../Common/ScreenLoader/FullScreenLoader";
import Button from "../Common/Button/Button";
import customConfig from "../../config";
import {
  createInAppNotification,
  updateInAppNotification,
  extractNotificationId,
  getDisplayTargetUserCount,
  getInAppNotificationErrorMessage,
  getInAppNotificationSaveSuccessMessage,
} from "../../action/inAppNotification";
import {
  buildApiPayload,
  extractTargetUserCountFromItem,
  isUserSpecificNotification,
  mapApiToForm,
  mergeFieldErrors,
  normalizeAssetCustomProperties,
  validateCtaField,
  validateExpiryDateWindow,
  validateMaxPerDayField,
  validateMaxPerWeekField,
  validateNotificationForm,
  validatePlatformField,
  validateRichTextBodyField,
  validateTitleField,
  getNotificationRichTextHelperText,
} from "../../utils/inAppNotificationUtils";
import {
  createEmptyCarouselScreen,
  createEmptyNotificationForm,
  DISPLAY_TYPE_OPTIONS,
  getInAppNotificationPermission,
  IN_APP_NOTIFICATION_VALIDATIONS,
  TARGETED_USERS_LABEL,
  DELIVERY_RADIO_COLUMN_WIDTHS,
  inAppFooterActionsSx,
  inAppRadioGroupResponsiveSx,
  pageAssetOnlyGridSx,
  pageFormShortInputSx,
} from "../../constants/inAppNotificationConstant";
import AssetFields from "./AssetFields";
import CtaEditor from "./CtaEditor";
import CarouselScreensSection from "./CarouselScreensSection";
import WysiwygEditorField from "./WysiwygEditorField";
import FormSection from "./FormSection";
import FormFieldRow from "./FormFieldRow";
import DeliverySettingsRow from "./DeliverySettingsRow";
import NotificationTargetUserSummary from "./NotificationTargetUserSummary";

const NotificationForm = ({
  mode,
  initialData = null,
  defaultTargetType = "broadcast",
  fallbackTargetUserCount,
  userRoles = [],
  userLevel = 0,
  onCancel,
  onSuccess,
  feedbackHandler = () => {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));
  const isReadOnly = mode === "view";
  const isCreateMode = mode === "create";
  const isEditMode = mode === "edit";

  const [formData, setFormData] = useState(createEmptyNotificationForm(defaultTargetType));
  const [errors, setErrors] = useState({});
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialData) {
      const nextForm = mapApiToForm(initialData, { defaultEmptyCta: mode !== "view" });
      setFormData(nextForm);
      setErrors({});
      setHasAttemptedSave(false);
    } else {
      setFormData(createEmptyNotificationForm(defaultTargetType));
      setErrors({});
      setHasAttemptedSave(false);
    }
  }, [initialData, defaultTargetType, mode]);

  const resolvedTargetType = initialData?.targetType ?? defaultTargetType;
  const displayTargetUserCount =
    getDisplayTargetUserCount(
      initialData?.id,
      initialData,
      fallbackTargetUserCount,
    ) ?? extractTargetUserCountFromItem(initialData);
  const canViewTargetUserCount = getInAppNotificationPermission(
    userRoles,
    "viewTargetUserCount",
    userLevel,
  );

  const handlePlatformChange = (platforms) => {
    if (isReadOnly || isEditMode) return;
    setFormData((prev) => ({ ...prev, platform: platforms }));
    setErrors((prev) =>
      mergeFieldErrors(prev, { platform: validatePlatformField(platforms) }),
    );
  };

  const handleSave = async () => {
    if (isReadOnly) return;

    const formForSave = { ...formData, targetType: resolvedTargetType };
    setHasAttemptedSave(true);
    const nextErrors = validateNotificationForm(formForSave);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      return;
    }

    const body = buildApiPayload(formForSave, { isUpdate: isEditMode });
    setLoading(true);

    try {
      const response = isEditMode
        ? await updateInAppNotification(initialData.id, body)
        : await createInAppNotification(body);

      const id = extractNotificationId(response?.data, initialData?.id);
      onSuccess?.({
        data: { ...body, id },
        message: getInAppNotificationSaveSuccessMessage(isEditMode, resolvedTargetType),
      });
    } catch (error) {
      feedbackHandler({
        open: true,
        type: "error",
        msg: getInAppNotificationErrorMessage(
          error,
          isEditMode ? customConfig.UPDATE_FAILURE : customConfig.ADD_FAILURE,
        ),
      });
    } finally {
      setLoading(false);
    }
  };

  const editorHeight = isMobile ? 96 : 108;
  const titleError = validateTitleField(formData.title, { required: hasAttemptedSave });
  const bodyError = validateRichTextBodyField(formData.body, { required: hasAttemptedSave });
  const bodyHelper = getNotificationRichTextHelperText(formData.body, {
    validationError: bodyError,
    maxLength: IN_APP_NOTIFICATION_VALIDATIONS.body.maxLength,
  });
  const maxPerDayError = validateMaxPerDayField(formData.config?.maxPerDay);
  const maxPerWeekError = validateMaxPerWeekField(formData.config?.maxPerWeek);

  return (
    <>
      {!isReadOnly && loading && <FullScreenLoader message={customConfig.UPDATING_DATA} />}

      {!isReadOnly && (
        <style>{`.react-datepicker-popper { z-index: 10006 !important; }`}</style>
      )}

      <Box
        sx={{
          width: "100%",
          minWidth: 0,
          maxWidth: "100%",
          boxSizing: "border-box",
          px: { xs: 1.5, md: 2 },
          pt: { xs: 1.25, md: 1.5 },
          pb: 0,
        }}
      >
        {canViewTargetUserCount &&
          (isReadOnly || isEditMode) &&
          initialData &&
          isUserSpecificNotification(resolvedTargetType) && (
            <NotificationTargetUserSummary
              targetUserCount={displayTargetUserCount}
              label={TARGETED_USERS_LABEL}
            />
          )}

        <FormSection variant="page" compact title="Basic information">
          <Grid2 container spacing={1.25} sx={{ width: "100%", m: 0 }}>
            <Grid2 size={{ xs: 12, md: 7, lg: 8 }}>
              <FormFieldRow
                stacked
                label="Title"
                required={!isReadOnly}
                error={titleError}
              >
                <FormInput
                  id="title"
                  placeholder="e.g., MirAIe Notifications"
                  value={formData.title}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, title: e.target.value }));
                  }}
                  error={false}
                  touched="false"
                  helperText={
                    !isReadOnly && !titleError
                      ? `${formData.title.length}/${IN_APP_NOTIFICATION_VALIDATIONS.title.maxLength} characters`
                      : undefined
                  }
                  fullWidth
                  size="small"
                  sx={pageFormShortInputSx}
                  disabled={isReadOnly}
                  slotProps={{
                    formHelperText: {
                      sx: {
                        color: "text.secondary",
                        marginLeft: 0,
                      },
                    },
                  }}
                />
              </FormFieldRow>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 5, lg: 4 }}>
              <FormFieldRow stacked label="Layout" required={isCreateMode}>
                {isCreateMode ? (
                  <AppRadioGroup
                    row={isSmUp}
                    fullWidth={false}
                    columnWidths={isSmUp ? DELIVERY_RADIO_COLUMN_WIDTHS : []}
                    name="type"
                    value={formData.type}
                    onChange={(e) => {
                      const type = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        type,
                        screens:
                          type === "Carousel" && !prev.screens.length
                            ? [createEmptyCarouselScreen()]
                            : prev.screens,
                      }));
                      setErrors((prev) => {
                        const next = { ...prev };
                        delete next.cta;
                        delete next.screens;
                        Object.keys(next).forEach((key) => {
                          if (key.startsWith("screen")) delete next[key];
                        });
                        return next;
                      });
                    }}
                    options={DISPLAY_TYPE_OPTIONS}
                    sx={inAppRadioGroupResponsiveSx}
                  />
                ) : (
                  <Typography
                    variant="body2"
                    sx={{
                      py: 1,
                      px: 0.5,
                      color: isReadOnly ? "text.secondary" : "text.primary",
                      fontWeight: 500,
                    }}
                  >
                    {formData.type || "—"}
                  </Typography>
                )}
              </FormFieldRow>
            </Grid2>
            <Grid2 size={12}>
              <FormFieldRow
                stacked
                label="Description"
                required={!isReadOnly}
                error={bodyError}
              >
                <WysiwygEditorField
                  value={formData.body}
                  onChange={(html) => {
                    setFormData((prev) => ({ ...prev, body: html }));
                  }}
                  error={false}
                  helperText={!isReadOnly && !bodyError ? bodyHelper.text : undefined}
                  helperTone="default"
                  height={editorHeight}
                  hideLabel
                  readOnly={isReadOnly}
                />
              </FormFieldRow>
            </Grid2>
          </Grid2>
        </FormSection>

        <FormSection variant="page" compact title="Delivery settings">
          <DeliverySettingsRow
            platform={formData.platform}
            onPlatformChange={handlePlatformChange}
            platformError={errors.platform}
            expiryDate={formData.expiryDate}
            onExpiryChange={(date) => {
              setFormData((prev) => ({ ...prev, expiryDate: date }));
              setErrors((prev) =>
                mergeFieldErrors(prev, { expiryDate: validateExpiryDateWindow(date) }),
              );
            }}
            expiryError={errors.expiryDate}
            maxPerDay={formData.config?.maxPerDay}
            onMaxPerDayChange={(value) => {
              if (isReadOnly) return;
              const parsed = Number.parseInt(String(value), 10);
              const nextValue = Number.isNaN(parsed) ? "" : parsed;
              setFormData((prev) => ({
                ...prev,
                config: {
                  ...prev.config,
                  maxPerDay: nextValue,
                },
              }));
            }}
            maxPerDayError={maxPerDayError}
            maxPerWeek={formData.config?.maxPerWeek}
            onMaxPerWeekChange={(value) => {
              if (isReadOnly) return;
              const parsed = Number.parseInt(String(value), 10);
              const nextValue = Number.isNaN(parsed) ? "" : parsed;
              setFormData((prev) => ({
                ...prev,
                config: {
                  ...prev.config,
                  maxPerWeek: nextValue,
                },
              }));
            }}
            maxPerWeekError={maxPerWeekError}
            readOnly={isReadOnly}
            platformReadOnly={isEditMode}
          />
        </FormSection>

        {formData.type === "Card" && (
          <FormSection variant="page" compact title="Card configuration">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
                width: "100%",
                minWidth: 0,
              }}
            >
              <Box sx={pageAssetOnlyGridSx}>
                <AssetFields
                  page
                  paired
                  assets={formData.assets}
                  alignActionRows={(formData.cta || []).length > 0}
                  onChange={(fieldOrPatch, value) => {
                    setFormData((prev) => {
                      const nextAssets =
                        fieldOrPatch &&
                        typeof fieldOrPatch === "object" &&
                        value === undefined
                          ? {
                              ...prev.assets,
                              ...fieldOrPatch,
                              customProperties: normalizeAssetCustomProperties(
                                fieldOrPatch.customProperties ??
                                  prev.assets?.customProperties,
                              ),
                            }
                          : {
                              ...prev.assets,
                              [fieldOrPatch]: value,
                            };

                      return {
                        ...prev,
                        assets: nextAssets,
                      };
                    });
                    setErrors((prev) =>
                      mergeFieldErrors(prev, {
                        asset: "",
                        assetCustomProperties: "",
                      }),
                    );
                  }}
                  errors={errors}
                  idPrefix="card-asset"
                  feedbackHandler={feedbackHandler}
                  readOnly={isReadOnly}
                />
              </Box>
              <CtaEditor
                page
                ctas={formData.cta}
                onChange={(cta) => {
                  setFormData((prev) => ({ ...prev, cta }));
                  setErrors((prev) => mergeFieldErrors(prev, { cta: validateCtaField(cta) }));
                }}
                maxCtas={IN_APP_NOTIFICATION_VALIDATIONS.maxCtasPerScreen}
                title="Call to action (optional)"
                idPrefix="card-cta"
                error={errors.cta}
                readOnly={isReadOnly}
              />
            </Box>
          </FormSection>
        )}

        {formData.type === "Carousel" && (
          <FormSection variant="page" compact title="Carousel configuration">
            <CarouselScreensSection
              page
              screens={formData.screens}
              hasAttemptedSave={hasAttemptedSave}
              onChange={(screensOrUpdater) =>
                setFormData((prev) => ({
                  ...prev,
                  screens:
                    typeof screensOrUpdater === "function"
                      ? screensOrUpdater(prev.screens)
                      : screensOrUpdater,
                }))
              }
              errors={errors}
              feedbackHandler={feedbackHandler}
              embedded
              readOnly={isReadOnly}
            />
          </FormSection>
        )}
      </Box>

      <Box
        sx={{
          ...inAppFooterActionsSx,
          px: { xs: 1.5, md: 2 },
          py: 1.25,
          bgcolor: "grey.50",
          borderTop: `1px solid ${theme.palette.divider}`,
          borderBottomLeftRadius: 16,
          borderBottomRightRadius: 16,
          position: "sticky",
          bottom: 0,
          zIndex: 2,
        }}
      >
        {isReadOnly ? (
          <Button variant="ghost" size="medium" onClick={onCancel}>
            Back
          </Button>
        ) : (
          <>
            <Button variant="ghost" size="medium" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="medium"
              onClick={handleSave}
              disabled={loading}
            >
              {isCreateMode ? "Create Notification" : "Update Notification"}
            </Button>
          </>
        )}
      </Box>
    </>
  );
};

export default NotificationForm;
